import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  SecurityOrder,
  CreateOrderParams,
  OrderValidationResponse,
  Orderbook,
  SecuritiesAccount,
  OrdersResponse,
} from '@/types/order'

/**
 * Hooks for order management and trading operations.
 */

// Query keys for cache management
export const orderKeys = {
  all: ['orders'] as const,
  myAccount: () => [...orderKeys.all, 'myAccount'] as const,
  myOrders: (accountId: string) => [...orderKeys.all, 'my', accountId] as const,
  orderbook: (asin: string) => [...orderKeys.all, 'orderbook', asin] as const,
}

/**
 * Hook to get current user's securities account.
 */
export function useMySecuritiesAccount() {
  return useQuery({
    queryKey: orderKeys.myAccount(),
    queryFn: () => api.get<SecuritiesAccount>('/api/v2/my/securitiesaccount'),
    staleTime: 300000, // Cache for 5 minutes
  })
}

/**
 * Hook to get orders for a securities account.
 */
export function useMyOrders(accountId: string | null) {
  return useQuery({
    queryKey: orderKeys.myOrders(accountId || ''),
    queryFn: () =>
      api.get<OrdersResponse>('/api/securityorders/', {
        owner: accountId!,
      }),
    enabled: !!accountId,
    staleTime: 10000, // Refresh orders frequently
  })
}

/**
 * Hook to get orderbook for a security.
 */
export function useOrderbook(securityIdentifier: string | null) {
  return useQuery({
    queryKey: orderKeys.orderbook(securityIdentifier || ''),
    queryFn: () => api.get<Orderbook>(`/api/orderbook/${securityIdentifier}`),
    enabled: !!securityIdentifier,
    staleTime: 5000, // Orderbooks change frequently
  })
}

/**
 * Hook for creating/validating orders.
 */
export function useCreateOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: CreateOrderParams) => {
      // Build query string
      const queryParams: Record<string, string | number | boolean | undefined> = {
        owner: params.owner,
        securityIdentifier: params.securityIdentifier,
        action: params.action,
        type: params.type,
        numberOfShares: params.numberOfShares,
      }

      if (params.price !== undefined) {
        queryParams.price = params.price
      }
      if (params.counterparty) {
        queryParams.counterparty = params.counterparty
      }
      if (params.goodAfterDate) {
        queryParams.goodAfterDate = params.goodAfterDate
      }
      if (params.goodTillDate) {
        queryParams.goodTillDate = params.goodTillDate
      }
      if (params.hourlyChange !== undefined) {
        queryParams.hourlyChange = params.hourlyChange
      }
      if (params.checkOrderOnly) {
        queryParams.checkOrderOnly = params.checkOrderOnly
      }

      return api.post<OrderValidationResponse | SecurityOrder>(
        '/api/securityorders/',
        undefined,
        queryParams
      )
    },
    onSuccess: (_, variables) => {
      // Invalidate orders cache after successful order
      if (!variables.checkOrderOnly) {
        queryClient.invalidateQueries({ queryKey: orderKeys.myOrders(variables.owner) })
      }
    },
  })
}

/**
 * Hook for deleting/canceling an order.
 */
export function useDeleteOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (orderId: string) => {
      return api.delete(`/api/securityorders/${orderId}`)
    },
    onSuccess: () => {
      // Invalidate all orders
      queryClient.invalidateQueries({ queryKey: orderKeys.all })
    },
  })
}
